import styled from '@emotion/styled';
import type { RecordingHighlightsListItem } from '@hotjar/webapp/domains/highlights/recording/model';
import { Button, Icon, Loader, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useImage } from '../SingleHighlight/use-image';
import { useRefreshedRecordingHighlight } from '../use-refreshed-recording-highlight';

const Wrapper = styled.div<{ image: string }>`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: url(${(props) => props.image}) no-repeat center center;
  background-size: cover;
`;

const NotDoneWrapper = styled.section<{ hasComments: boolean }>`
  color: ${tokens.color.text_default_inverse};
  align-content: center;
  display: grid;
  grid-template-rows: auto auto auto;
  grid-row-gap: ${tokens.spacer.x2};
  justify-items: center;
  padding: ${tokens.spacer.x2};
  font-size: 12px;
`;

interface RecordingHighlightPreviewProps {
  className?: string;
  hasComments: boolean;
  highlight: RecordingHighlightsListItem;
  siteId: string;
}

function getRecordingHighlightDurationText(length?: number): string {
  if (!length) {
    return 'Play';
  }
  const date = new Date(0);
  date.setSeconds(length / 1000); // specify value for SECONDS here
  let duration = date.toISOString().substr(14, 5);
  duration = duration.startsWith('00:') ? duration.substr(3, 2) : duration;
  duration = `${duration.replace(':', ' min ')} sec`;
  return duration;
}

export function RecordingHighlightPreview({
  className,
  highlight: initialHighlight,
  hasComments,
  siteId,
}: RecordingHighlightPreviewProps): JSX.Element {
  const { t } = useTranslation();
  const { data: highlight } = useRefreshedRecordingHighlight(siteId, initialHighlight);
  const { encodedImage } = useImage(highlight.imageUrl ?? '', true);

  switch (highlight.contentStatus) {
    case 'failed':
      return (
        <NotDoneWrapper className={className} hasComments={hasComments}>
          <Icon icon="error" size="l" />
          {t('highlights:single.preview.content-generation.failed')}
        </NotDoneWrapper>
      );
    case 'source_deleted':
      return (
        <NotDoneWrapper className={className} hasComments={hasComments}>
          <Icon icon="error" size="l" />
          {t('highlights:single.preview.content-generation.source-deleted')}
        </NotDoneWrapper>
      );
    case 'creating':
      return (
        <NotDoneWrapper className={className} hasComments={hasComments}>
          <Loader size="l" />
          {t('highlights:single.preview.content-generation.creating')}
        </NotDoneWrapper>
      );
    case 'done':
      if (highlight.imageUrl) {
        return (
          <Wrapper image={encodedImage}>
            <Button size="s" icon="play_alt">
              {`${getRecordingHighlightDurationText(highlight.metadata.clip?.duration)}`}
            </Button>
          </Wrapper>
        );
      }
      return (
        <NotDoneWrapper className={className} hasComments={hasComments}>
          <Icon icon="error" size="l" />
          {t('highlights:single.image_not_available')}
        </NotDoneWrapper>
      );
    default:
      return (
        <NotDoneWrapper className={className} hasComments={hasComments}>
          <Icon icon="error" size="l" />
          {t('highlights:single.preview.content-generation.unknown')}
        </NotDoneWrapper>
      );
  }
}
